/*
 * <insurance-modal-hybrid> — hybrid insurance coverage modal.
 *
 * Combines the broadly-used iSleep/Dreem flow with the in-house Canvas flow, and
 * lets the server decide which one each patient gets:
 *
 * THREE destinations, and which one a shopper gets is not a detail:
 *   1. Medicare / Medicaid / Medi-Cal  → iSleep contact form → iSleep booking.
 *      Short-circuited BEFORE /route is called, so the worker never sees them.
 *   2. In-house capable + capacity      → Canvas Zoho intake (?PrimaryPayor=&PayorID=)
 *   3. Everything else                  → the STANDARD insurance intake form
 *
 * (3) is the important one: a commercial-payer shopper we can't serve in house —
 * no Bridge coverage, state not opened, no NP capacity, stale feed — belongs in
 * the usual insurance intake, NOT the iSleep Medicare flow. Because (1) never
 * reaches /route, every /route reply is a non-Medicare shopper, which is what
 * makes `route: "isleep"` safe to render as the standard intake.
 *
 *   state → provider autocomplete (Bridge payer search) → POST {worker}/route
 *     route "canvas"     → Canvas intake redirect
 *     route "isleep"     → standard insurance intake redirect (NOT iSleep)
 *     route "ineligible" → coverage-unavailable screen
 *
 * The routing decision is deliberately NOT made here. Whether we can see a patient
 * in house depends on Bridge coverage for BOTH provider types AND a real bookable
 * Nurse Practitioner appointment slot in their state — a shared, cached signal no
 * browser can hold. The worker owns it; this file only renders the answer.
 * See bridge-proxy/README.md.
 *
 * The worker's `reason` explains any non-canvas decision and rides along on the
 * `route_decided` analytics event (it never changes what the shopper sees):
 *   not_in_house_capable     Bridge doesn't cover both provider types in the state
 *   state_not_enabled        state isn't in the phased-rollout allowlist yet
 *   no_np_availability       no bookable NP slot in the state within the horizon
 *   np_capacity_reserved     slots exist but this state's snapshot budget is spent
 *   pool_capacity_reserved   the shared roster-wide appointment pool is spent
 *   state_not_serviceable    state isn't Go-Live for in-house telehealth
 *   availability_unavailable no trustworthy capacity signal (fails closed)
 *   at_capacity              the optional global daily ceiling is full
 *   routing_disabled         kill switch
 *
 * Medicare/Medicaid short-circuits to iSleep before /route is called: those
 * patients always go to iSleep, so they shouldn't spend a Bridge call or consume
 * an internal slot on the way out.
 *
 * Cash Pay is available on every step ([data-cash-pay]) — the patient never has to
 * resolve their insurance to keep buying.
 *
 * Config arrives as data-* attributes on the element (same pattern as
 * assets/insurance-coverage.js). Redirect params come from
 * window.SDFormTracking (assets/sd-form-tracking.js).
 *
 * Markup: snippets/product-insurance-modal-hybrid.liquid
 * Styles: assets/sleepdoctor-medicare-modal.css (class-scoped) +
 *         assets/sleepdoctor-medicare-modal-v2.css (element-scoped — this element
 *         is named in those selector lists alongside the two sibling modals).
 */
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 749;
  const SEARCH_DEBOUNCE_MS = 250;
  const SEARCH_MIN_CHARS = 2;

  /* Payers that always route to iSleep, checked before the worker call. */
  const ISLEEP_NAME_RE = /medicare|medicaid|medi-?cal/i;

  const ARROW_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M15.3431 4.92894L21.7071 11.2929C22.0976 11.6834 22.0976 12.3166 21.7071 12.7071L15.3431 19.0711C14.9526 19.4616 14.3195 19.4616 13.9289 19.0711C13.5384 18.6806 13.5384 18.0474 13.9289 17.6569L18.5858 13H2.5C1.94772 13 1.5 12.5523 1.5 12C1.5 11.4477 1.94772 11 2.5 11H18.5858L13.9289 6.34315C13.5384 5.95263 13.5384 5.31947 13.9289 4.92894C14.3195 4.53842 14.9526 4.53842 15.3431 4.92894Z" fill="currentColor"/></svg>';

  function appendParam(url, key, value) {
    if (!url || !value) return url;
    return (
      url +
      (url.indexOf('?') > -1 ? '&' : '?') +
      encodeURIComponent(key) +
      '=' +
      encodeURIComponent(value)
    );
  }

  class InsuranceModalHybrid extends HTMLElement {
    constructor() {
      super();
      this.selectedState = ''; /* 2-letter code — what Bridge expects */
      this.selectedPayerId = '';
      this.selectedPayerName = '';
      this.currentStep = 1;
      this.isleepReason = ''; /* 'medicare' | 'overflow' — drives the intro copy */
      this.isleepFormStarted = false;
      this.searchSeq = 0;
      this.searchTimer = null;
    }

    connectedCallback() {
      this.workerUrl = (this.getAttribute('data-worker-url') || '').replace(/\/$/, '');
      this.canvasFormUrl = this.getAttribute('data-canvas-form-url') || '';
      this.standardIntakeUrl = this.getAttribute('data-standard-intake-url') || '';
      this.payerParam = this.getAttribute('data-payer-param') || 'PayorID';
      this.primaryPayorParam = this.getAttribute('data-primary-payor-param') || 'PrimaryPayor';
      this.checkingText = this.getAttribute('data-checking-text') || 'Checking coverage…';
      this.externalTriggerSelector = this.getAttribute('data-external-trigger') || '';
      this.dreemWorkerUrl = this.getAttribute('data-dreem-worker-url') || '';
      this.dreemRedirectUrl = this.getAttribute('data-dreem-redirect-url') || '';
      this.isleepButtonText =
        this.getAttribute('data-isleep-button-text') || 'Verify with iSleep Physicians';
      this.cashCartUrl = this.getAttribute('data-cash-cart-url') || '';
      this.cashPrice = this.getAttribute('data-cash-price') || '';

      this.ctaButton = this.querySelector('.sd-medicare-cta');
      this.overlay = this.querySelector('.sd-medicare-modal-overlay');
      this.modal = this.querySelector('.sd-medicare-modal-container');
      this.closeButton = this.querySelector('.sd-medicare-modal-close');
      this.steps = this.querySelectorAll('.sd-medicare-modal-step');

      /* Step 1 */
      this.stateDropdown = this.querySelector('[data-state-dropdown]');
      this.providerInput = this.querySelector('[data-provider-input]');
      this.providerList = this.querySelector('[data-autocomplete-list]');
      this.continueButton = this.querySelector('[data-step1-continue]');
      this.continueInitialHTML = this.continueButton.innerHTML;

      /* iSleep contact step */
      this.isleepForm = this.querySelector('.sd-medicare-dreem-form');
      this.isleepContinueBtn = this.querySelector('.sd-medicare-btn-dreem-continue');
      this.isleepCancelBtn = this.querySelector('[data-isleep-cancel]');
      this.isleepIntros = this.querySelectorAll('[data-isleep-info]');

      /* Header — swapped when the flow moves to the iSleep step, restored on reset */
      this.badgeText = this.querySelector('[data-badge-text]');
      this.headingEl = this.querySelector('[data-heading]');
      this.subtitleEl = this.querySelector('[data-subtitle]');
      this.defaultIcon = this.querySelector('.sd-medicare-badge-icon-default');
      this.checkIcon = this.querySelector('.sd-medicare-badge-icon-check');
      this.headerDefaults = {
        badge: this.badgeText.textContent,
        heading: this.headingEl.textContent,
        subtitle: this.subtitleEl.textContent,
      };
      /* The subtitle is intentionally cleared rather than settable: the info card
         directly below carries the explanatory copy on this step, so a header
         subtitle would just repeat it. Matches the Bridge modal. */
      this.isleepHeader = {
        badge: this.getAttribute('data-isleep-badge') || 'YOU MAY BE COVERED',
        heading: this.getAttribute('data-isleep-heading') || 'Check your coverage',
        subtitle: '',
      };

      /* No cash URL configured — remove the escape rather than offer a dead link. */
      if (!this.cashCartUrl) {
        this.querySelectorAll('[data-cash-pay]').forEach((btn) => btn.remove());
      }

      this.setupEventListeners();

      /* Deep link: ?modal=true opens on load. */
      try {
        if (new URLSearchParams(window.location.search).get('modal') === 'true') {
          this.openModal();
        }
      } catch {
        /* URL parsing is best-effort; never block render */
      }
    }

    setupEventListeners() {
      if (this.ctaButton) {
        this.ctaButton.addEventListener('click', () => {
          this.push({ event_action: 'open_button_click', modal_step: 'pre_modal' });
          this.openModal();
        });
      }

      /* External triggers — existing page buttons open the modal. Delegated on
         document so it works wherever they sit and catches ones injected later. */
      if (this.externalTriggerSelector) {
        document.addEventListener('click', (e) => {
          const trigger =
            e.target && e.target.closest ? e.target.closest(this.externalTriggerSelector) : null;
          if (!trigger) return;
          e.preventDefault();
          this.push({ event_action: 'open_button_click', modal_step: 'pre_modal' });
          this.openModal();
        });
      }

      this.closeButton.addEventListener('click', () => this.closeModal('x_button'));
      this.overlay.addEventListener('click', () => this.closeModal('overlay_click'));

      this.initDropdown();
      this.initAutocomplete();

      this.handleOutsideClick = (e) => {
        if (!e.target.closest('.sd-insurance-dropdown')) {
          this.closeDropdown();
          this.closeProviderList();
        }
      };
      document.addEventListener('click', this.handleOutsideClick);

      /* Continue — Medicare/Medicaid short-circuit, everyone else asks the worker. */
      this.continueButton.addEventListener('click', () => {
        if (ISLEEP_NAME_RE.test(this.selectedPayerName)) {
          this.goToIsleep('medicare');
        } else {
          this.decideRoute();
        }
      });

      /* Cash Pay — same handler on every step, tagged with the step it came from. */
      this.querySelectorAll('[data-cash-pay]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToCash();
        });
      });

      this.isleepContinueBtn.addEventListener('click', () => this.submitIsleep());
      this.isleepCancelBtn.addEventListener('click', () => {
        this.push({ event_action: 'cancel_close', modal_step: 'step_2_isleep_form' });
        this.closeModal('cancel_close');
      });
      this.isleepForm.querySelectorAll('.sd-medicare-form-input').forEach((input) => {
        input.addEventListener('input', () => {
          const group = input.closest('.sd-medicare-form-group');
          if (group) group.classList.remove('has-error');
        });
        input.addEventListener('focus', () => {
          if (!this.isleepFormStarted) {
            this.isleepFormStarted = true;
            this.push({ event_action: 'form_started', modal_step: 'step_2_isleep_form' });
          }
        });
        input.addEventListener('blur', () => {
          if (input.value.trim() !== '') {
            const fieldName =
              (input.name || '').replace(/^contact\[(\w+)\]$/, '$1') || input.id;
            this.push({
              event_action: 'form_field_completed',
              modal_step: 'step_2_isleep_form',
              form_field_name: fieldName,
            });
          }
        });
      });

      /* Ineligible: back to step 1, selections intact so a retry is one edit away. */
      this.querySelectorAll('[data-ineligible-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.goToStep1());
      });

      this.querySelectorAll('.sd-insurance-error-close').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal('error_close'));
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('active')) {
          this.closeModal('x_button');
        }
      });
    }

    /* ---- State dropdown ---- */

    initDropdown() {
      const root = this.stateDropdown;
      const trigger = root.querySelector('[data-dropdown-trigger]');

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (root.classList.contains('is-open')) {
          this.closeDropdown();
        } else {
          this.closeProviderList();
          this.openDropdown();
        }
      });
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeDropdown();
      });

      root.querySelectorAll('.sd-insurance-dropdown-option').forEach((opt) => {
        opt.addEventListener('click', () => this.selectStateOption(opt));
        opt.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectStateOption(opt);
          }
        });
      });
    }

    openDropdown() {
      const root = this.stateDropdown;
      root.classList.add('is-open');
      root.querySelector('[data-dropdown-trigger]').setAttribute('aria-expanded', 'true');
      root.querySelector('[data-dropdown-list]').hidden = false;
    }

    closeDropdown() {
      const root = this.stateDropdown;
      root.classList.remove('is-open');
      const trigger = root.querySelector('[data-dropdown-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      const list = root.querySelector('[data-dropdown-list]');
      if (list) list.hidden = true;
    }

    selectStateOption(opt) {
      const root = this.stateDropdown;
      const valueEl = root.querySelector('[data-dropdown-value]');
      const label = opt.querySelector('.sd-insurance-dropdown-option-title').textContent;
      const val = opt.getAttribute('data-value');
      root.setAttribute('data-value', val);
      valueEl.textContent = label;
      valueEl.classList.remove('is-placeholder');
      root.querySelectorAll('.sd-insurance-dropdown-option').forEach((o) => {
        o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
      });
      this.closeDropdown();
      this.selectedState = val;
      /* Provider search is state-scoped, so it stays disabled until now. */
      this.providerInput.disabled = false;
      this.push({
        event_action: 'state_selected',
        modal_step: 'step_1_selection',
        shipping_state: val,
      });
      this.updateContinueState();
    }

    clearStateDropdown() {
      const root = this.stateDropdown;
      root.removeAttribute('data-value');
      const valueEl = root.querySelector('[data-dropdown-value]');
      valueEl.textContent = valueEl.getAttribute('data-placeholder') || '';
      valueEl.classList.add('is-placeholder');
      root.querySelectorAll('.sd-insurance-dropdown-option').forEach((o) => {
        o.setAttribute('aria-selected', 'false');
      });
      this.selectedState = '';
    }

    /* ---- Provider autocomplete (Bridge payer search via the worker) ---- */

    initAutocomplete() {
      this.providerInput.addEventListener('input', () => {
        /* Any edit invalidates the prior pick — a free-text payer name is not a
           payer id, and the worker needs the id. */
        this.selectedPayerId = '';
        this.selectedPayerName = '';
        this.updateContinueState();
        this.scheduleSearch(this.providerInput.value.trim());
      });

      this.providerInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeProviderList();
        } else if (e.key === 'ArrowDown') {
          const first = this.providerList.querySelector(
            '.sd-insurance-dropdown-option:not(.is-message)'
          );
          if (first) {
            e.preventDefault();
            first.focus();
          }
        }
      });
      /* The list opens only in response to typing, never on focus — otherwise
         refocusing right after a selection would immediately reopen the results. */
    }

    scheduleSearch(query) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      if (query.length < SEARCH_MIN_CHARS) {
        this.closeProviderList();
        return;
      }
      this.searchTimer = setTimeout(() => this.searchPayers(query), SEARCH_DEBOUNCE_MS);
    }

    async searchPayers(query) {
      if (!this.workerUrl) {
        this.renderProviderMessage('Search is unavailable right now.');
        return;
      }
      const seq = ++this.searchSeq;
      this.renderProviderMessage('Searching…');
      try {
        const res = await fetch(this.workerUrl + '/payers/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: query, limit: 10 }),
        });
        /* Guard every await: a slower earlier query must not overwrite a newer
           one's results. */
        if (seq !== this.searchSeq) return;
        if (!res.ok) throw new Error('Payer search failed: ' + res.status);
        const data = await res.json();
        if (seq !== this.searchSeq) return;
        this.renderProviderResults((data && data.items) || []);
      } catch (err) {
        if (seq !== this.searchSeq) return;
        console.error('Bridge payer search failed:', err);
        this.renderProviderMessage('We couldn’t load providers. Try again.');
      }
    }

    renderProviderResults(items) {
      if (!items.length) {
        this.renderProviderMessage('No matching providers found.');
        return;
      }
      this.providerList.innerHTML = '';
      items.forEach((p) => {
        const li = document.createElement('li');
        li.className = 'sd-insurance-dropdown-option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '0');
        li.setAttribute('aria-selected', 'false');
        li.dataset.payerId = p.id || '';
        li.dataset.payerName = p.name || '';
        const title = document.createElement('span');
        title.className = 'sd-insurance-dropdown-option-title';
        title.textContent = p.name || '';
        li.appendChild(title);
        if (p.hint) {
          const desc = document.createElement('span');
          desc.className = 'sd-insurance-dropdown-option-desc';
          desc.textContent = p.hint;
          li.appendChild(desc);
        }
        li.addEventListener('click', () => this.selectPayer(li));
        li.addEventListener('keydown', (e) => this.onOptionKeydown(e, li));
        this.providerList.appendChild(li);
      });
      this.openProviderList();
    }

    renderProviderMessage(message) {
      this.providerList.innerHTML = '';
      const li = document.createElement('li');
      li.className = 'sd-insurance-dropdown-option is-message';
      li.setAttribute('aria-disabled', 'true');
      const title = document.createElement('span');
      title.className = 'sd-insurance-dropdown-option-title';
      title.textContent = message;
      li.appendChild(title);
      this.providerList.appendChild(li);
      this.openProviderList();
    }

    onOptionKeydown(e, li) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.selectPayer(li);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = li.nextElementSibling;
        if (next && !next.classList.contains('is-message')) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = li.previousElementSibling;
        if (prev && !prev.classList.contains('is-message')) prev.focus();
        else this.providerInput.focus();
      } else if (e.key === 'Escape') {
        this.closeProviderList();
        this.providerInput.focus();
      }
    }

    selectPayer(li) {
      this.selectedPayerId = li.dataset.payerId || '';
      this.selectedPayerName = li.dataset.payerName || '';
      this.providerInput.value = this.selectedPayerName;
      this.closeProviderList();
      /* Drop the rendered results so nothing can re-surface them after select. */
      this.providerList.innerHTML = '';
      this.push({
        event_action: 'insurance_selected',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        payer_id: this.selectedPayerId,
      });
      this.updateContinueState();
      this.providerInput.focus();
    }

    openProviderList() {
      this.providerList.hidden = false;
      this.providerInput.setAttribute('aria-expanded', 'true');
    }

    closeProviderList() {
      this.providerList.hidden = true;
      this.providerInput.setAttribute('aria-expanded', 'false');
    }

    updateContinueState() {
      this.continueButton.disabled = !(this.selectedState && this.selectedPayerId);
    }

    /* ---- Routing decision ---- */

    async decideRoute() {
      if (!this.workerUrl) {
        this.showError();
        return;
      }
      this.continueButton.disabled = true;
      this.continueButton.textContent = this.checkingText;
      this.push({
        event_action: 'route_decision_started',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        shipping_state: this.selectedState,
      });
      try {
        const res = await fetch(this.workerUrl + '/route', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payerId: this.selectedPayerId,
            state: this.selectedState,
            payerName: this.selectedPayerName,
            /* No enabledStates: the worker's CANVAS_ENABLED_STATES is the only rollout list. */
          }),
        });
        if (!res.ok) throw new Error('Route decision failed: ' + res.status);
        const data = await res.json();
        this.push({
          event_action: 'route_decided',
          modal_step: 'step_1_selection',
          insurance_carrier: this.selectedPayerName,
          payer_id: this.selectedPayerId,
          shipping_state: this.selectedState,
          route: data.route || '',
          reason: data.reason || '',
          in_house_capable: !!data.inHouseCapable,
          priority_state: !!data.priorityState,
        });

        if (data.route === 'canvas') {
          /* Navigates away — leave the button in its loading state. */
          this.submitCanvas();
        } else if (data.route === 'ineligible') {
          this.restoreContinue();
          this.showIneligible();
        } else {
          /* Anything that isn't in-house goes to the STANDARD insurance intake
             form — not iSleep. Medicare/Medicaid short-circuited before this
             request was ever made, so every /route reply is a non-Medicare
             shopper, and sending them into the iSleep Medicare flow would be
             wrong. An unrecognized route lands here too: still a real
             destination, and the correct one for a commercial payer. */
          this.submitStandardIntake();
        }
      } catch (err) {
        console.error('Route decision failed:', err);
        this.push({
          event_action: 'worker_error',
          modal_step: 'step_1_selection',
          error_phase: 'route',
        });
        this.restoreContinue();
        this.showError();
      }
    }

    /* Canvas → append the payer + tracking ids to the intake URL and redirect. */
    submitCanvas() {
      if (!this.canvasFormUrl) {
        this.restoreContinue();
        this.showError();
        return;
      }
      this.push({
        event_action: 'continue_with_insurance_click',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        payer_id: this.selectedPayerId,
        intake_destination: 'canvas',
        redirect_url: this.canvasFormUrl,
      });
      let url = appendParam(this.canvasFormUrl, this.primaryPayorParam, this.selectedPayerName);
      url = appendParam(url, this.payerParam, this.selectedPayerId);
      this.continueButton.disabled = true;
      this.continueButton.textContent = 'Redirecting…';
      if (window.SDFormTracking && window.SDFormTracking.appendIds) {
        window.SDFormTracking.appendIds(url, function (finalUrl) {
          window.location.href = finalUrl;
        });
      } else {
        window.location.href = url;
      }
    }

    /* Not in-house → the usual insurance intake form. Matches how the HSS modal's
       "Continue with insurance" redirects: tracking params only, no payer params,
       because that form doesn't consume them. */
    submitStandardIntake() {
      if (!this.standardIntakeUrl) {
        /* No form configured — fall back to the iSleep contact form rather than
           dead-ending the shopper. Not the intended path: set the URL. */
        console.warn('insurance-modal-hybrid: no standard intake URL set; falling back to the iSleep form.');
        this.restoreContinue();
        this.goToIsleep('overflow');
        return;
      }
      this.push({
        event_action: 'continue_with_insurance_click',
        modal_step: 'step_1_selection',
        insurance_carrier: this.selectedPayerName,
        payer_id: this.selectedPayerId,
        intake_destination: 'standard_intake',
        redirect_url: this.standardIntakeUrl,
      });
      this.continueButton.disabled = true;
      this.continueButton.textContent = 'Redirecting…';
      const url = this.standardIntakeUrl;
      if (window.SDFormTracking && window.SDFormTracking.appendIds) {
        window.SDFormTracking.appendIds(url, function (finalUrl) {
          window.location.href = finalUrl;
        });
      } else {
        window.location.href = url;
      }
    }

    /* ---- Cash Pay (available on every step) ---- */

    /* partnerID rides along as a CART ATTRIBUTE here, not a query id — this is a
       cart URL, not a form URL, which is why SDFormTracking exposes getPartnerId()
       separately from appendIds(). */
    cashUrlWithPartner() {
      if (!this.cashCartUrl) return '';
      const pid =
        window.SDFormTracking && window.SDFormTracking.getPartnerId
          ? window.SDFormTracking.getPartnerId()
          : '';
      if (!pid) return this.cashCartUrl;
      const sep = this.cashCartUrl.indexOf('?') > -1 ? '&' : '?';
      return this.cashCartUrl + sep + 'attributes[partnerID]=' + encodeURIComponent(pid);
    }

    goToCash() {
      this.push({
        event_action: 'continue_without_insurance_click',
        modal_step: 'step_' + this.currentStep,
        cash_price: this.cashPrice,
      });
      const url = this.cashUrlWithPartner();
      if (url) {
        window.location.href = url;
      } else {
        this.closeModal('cash_no_url');
      }
    }

    /* ---- iSleep contact step ---- */

    setIsleepHeader() {
      this.badgeText.textContent = this.isleepHeader.badge;
      this.headingEl.textContent = this.isleepHeader.heading;
      this.subtitleEl.textContent = this.isleepHeader.subtitle;
      if (this.defaultIcon) this.defaultIcon.style.display = 'none';
      if (this.checkIcon) this.checkIcon.style.display = '';
    }

    restoreHeader() {
      this.badgeText.textContent = this.headerDefaults.badge;
      this.headingEl.textContent = this.headerDefaults.heading;
      this.subtitleEl.textContent = this.headerDefaults.subtitle;
      if (this.defaultIcon) this.defaultIcon.style.display = '';
      if (this.checkIcon) this.checkIcon.style.display = 'none';
    }

    /* reason: 'medicare' (short-circuited) | 'overflow' (worker sent them here) —
       the two arrive for different causes and read differently to the patient. */
    goToIsleep(reason) {
      this.isleepReason = reason;
      this.currentStep = 2;
      this.updateStepDisplay();
      this.setIsleepHeader();
      this.isleepIntros.forEach((el) => {
        const matches = el.getAttribute('data-isleep-info') === reason;
        el.hidden = !matches;
      });
      this.push({
        event_action: 'step_viewed',
        modal_step: 'step_2_isleep_form',
        coverage_type_selected: this.selectedPayerName,
        intake_destination: 'isleep_medicare',
        isleep_reason: reason,
      });
    }

    async submitIsleep() {
      this.clearErrors(this.isleepForm);
      if (!this.validateFormEl(this.isleepForm)) return;
      this.isleepContinueBtn.disabled = true;
      this.isleepContinueBtn.textContent = 'Redirecting…';
      try {
        const emailInput = this.isleepForm.querySelector('input[type="email"]');
        const emailHash = emailInput ? await this.hashEmail(emailInput.value.trim()) : '';
        await this.submitIsleepForm();
        this.push({
          event_action: 'isleep_form_submitted',
          modal_step: 'step_2_isleep_form',
          coverage_type_selected: this.selectedPayerName,
          isleep_reason: this.isleepReason,
          user_email_hash: emailHash,
        });
        this.push({
          event_action: 'isleep_redirect_initiated',
          modal_step: 'step_2_isleep_form',
          coverage_type_selected: this.selectedPayerName,
          redirect_url: this.dreemRedirectUrl,
        });
        if (this.dreemRedirectUrl) {
          window.location.href = this.dreemRedirectUrl;
        } else {
          this.showError();
        }
      } catch (err) {
        console.error('iSleep referral submission failed:', err);
        this.isleepContinueBtn.disabled = false;
        this.isleepContinueBtn.innerHTML = this.isleepButtonText + ' ' + ARROW_SVG;
      }
    }

    async submitIsleepForm() {
      const form = this.isleepForm;
      const nameInput = form.querySelector('input[name="contact[name]"]');
      const emailInput = form.querySelector('input[type="email"]');
      const phoneInput = form.querySelector('input[name="contact[phone]"]');
      const response = await fetch(this.dreemWorkerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nameInput ? nameInput.value.trim() : '',
          email: emailInput ? emailInput.value.trim() : '',
          phone: phoneInput ? phoneInput.value.trim() : '',
          medicare_type: this.selectedPayerName || 'unknown',
          state: this.selectedState,
          referral_reason: this.isleepReason,
          source: 'sleepdoctor_insurance_modal_hybrid',
        }),
      });
      if (!response.ok) {
        throw new Error('iSleep referral failed: ' + response.status);
      }
      return response.json();
    }

    validateFormEl(form) {
      let valid = true;
      form.querySelectorAll('.sd-medicare-form-input').forEach((input) => {
        const group = input.closest('.sd-medicare-form-group');
        if (input.value.trim() === '') {
          group.classList.add('has-error');
          valid = false;
        } else if (
          input.type === 'email' &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim())
        ) {
          group.classList.add('has-error');
          valid = false;
        }
      });
      return valid;
    }

    clearErrors(form) {
      form.querySelectorAll('.sd-medicare-form-group').forEach((group) => {
        group.classList.remove('has-error');
      });
    }

    async hashEmail(email) {
      const encoded = new TextEncoder().encode(email.toLowerCase().trim());
      const buffer = await crypto.subtle.digest('SHA-256', encoded);
      return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }

    /* ---- Step display ---- */

    updateStepDisplay() {
      this.steps.forEach((step) => {
        step.classList.toggle('active', step.getAttribute('data-step') === String(this.currentStep));
      });
    }

    restoreContinue() {
      this.continueButton.disabled = false;
      this.continueButton.innerHTML = this.continueInitialHTML;
    }

    showError() {
      this.currentStep = 'error';
      this.updateStepDisplay();
    }

    showIneligible() {
      this.currentStep = 'ineligible';
      this.updateStepDisplay();
      this.push({
        event_action: 'coverage_unavailable',
        modal_step: 'step_ineligible',
        insurance_carrier: this.selectedPayerName,
      });
    }

    goToStep1() {
      this.currentStep = 1;
      this.updateStepDisplay();
      this.restoreHeader();
      this.restoreContinue();
    }

    /* ---- Analytics + open/close ---- */

    push(params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign(
          {
            event: 'insurance_modal_hybrid',
            device_type: window.innerWidth <= MOBILE_BREAKPOINT ? 'mobile' : 'desktop',
          },
          params
        )
      );
    }

    openModal() {
      this.overlay.classList.add('active');
      this.modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.push({ event_action: 'modal_open', modal_step: 'step_1_selection' });
    }

    closeModal(closeMethod) {
      this.push({
        event_action: 'modal_abandoned',
        modal_step: 'step_' + this.currentStep,
        close_method: closeMethod || 'x_button',
      });
      this.overlay.classList.remove('active');
      this.modal.classList.remove('active');
      document.body.style.overflow = '';
      setTimeout(() => this.resetModal(), 300);
    }

    resetModal() {
      this.currentStep = 1;
      this.updateStepDisplay();
      this.restoreHeader();

      this.clearStateDropdown();
      this.closeDropdown();
      this.selectedPayerId = '';
      this.selectedPayerName = '';
      this.providerInput.value = '';
      this.providerInput.disabled = true;
      this.providerList.innerHTML = '';
      this.closeProviderList();
      /* Invalidate any search still in flight so it can't render into a reset modal. */
      this.searchSeq++;
      this.continueButton.disabled = true;
      this.continueButton.innerHTML = this.continueInitialHTML;

      this.isleepReason = '';
      this.isleepFormStarted = false;
      this.clearErrors(this.isleepForm);
      this.isleepForm.querySelectorAll('.sd-medicare-form-input').forEach((i) => {
        i.value = '';
      });
      this.isleepContinueBtn.disabled = false;
      this.isleepContinueBtn.innerHTML = this.isleepButtonText + ' ' + ARROW_SVG;
    }
  }

  if (!customElements.get('insurance-modal-hybrid')) {
    customElements.define('insurance-modal-hybrid', InsuranceModalHybrid);
  }
})();
